import React, { useState, useEffect } from 'react';
import { LOGO_URL, API } from '../utils/constants';
import { showWelcomeToast } from '../utils/helpers';
import { setUserSession } from '../utils/session';
import * as api from '../services/apiService';
import { APP_VERSION } from '../version';
import axios from 'axios';
import RegistrationForm from './RegistrationForm';
import EmployeeFormModal from './EmployeeFormModal';
import QuickLossModal from './QuickLossModal';
import QuickGlassLossModal from './QuickGlassLossModal';
import ProblemReportModal from './ProblemReportModal';
import JerryDrivePublic from './public/JerryDrivePublic';
import JerryDriveAccessLock from './public/JerryDriveAccessLock';
import MattersFab from './MattersFab';

// MR JERRY Background Logo
const BG_LOGO_URL = 'https://customer-assets.emergentagent.com/job_tip-tracker-8/artifacts/75sm08c5_ChatGPT%20Image%2016%20mar%202026%2C%2014_39_06.png';

const LoginScreen = ({ onLogin, autoOpenDevPanel = false, onDevPanelClose = null }) => {
  const [location, setLocation] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [showQuickLoss, setShowQuickLoss] = useState(false);
  const [showQuickGlassLoss, setShowQuickGlassLoss] = useState(false);
  const [showProblemReport, setShowProblemReport] = useState(false);
  const [showJerryDrive, setShowJerryDrive] = useState(false);
  const [jdLock, setJdLock] = useState({ open: false, type: null, error: '' });
  const [showDevPanel, setShowDevPanel] = useState(autoOpenDevPanel);
  const [devPin, setDevPin] = useState('');
  const [devPinError, setDevPinError] = useState('');
  const [devStep, setDevStep] = useState('pin'); // 'pin' | 'location' | 'user'
  const [devUsers, setDevUsers] = useState([]);
  const [devUsersLoading, setDevUsersLoading] = useState(false);
  const [devSelectedLocation, setDevSelectedLocation] = useState('');
  const [devUserSearch, setDevUserSearch] = useState('');
  // Ograniczenie lokalizacji na podstawie typu PIN (master vs user)
  const [devAllowedLocations, setDevAllowedLocations] = useState(null); // null = brak restrykcji
  
  // Database status
  const [dbStatus, setDbStatus] = useState({ connected: null, checking: true });

  // Check database status
  const checkDatabaseStatus = async () => {
    try {
      const response = await axios.get(`${API}/database/status`);
      setDbStatus({ 
        connected: response.data.connected, 
        checking: false,
        message: response.data.message 
      });
    } catch (error) {
      setDbStatus({ 
        connected: false, 
        checking: false,
        message: 'Błąd połączenia z bazą danych' 
      });
    }
  };

  // Auto-check database status every 10 seconds
  useEffect(() => {
    checkDatabaseStatus();
    const interval = setInterval(checkDatabaseStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  // Manual refresh database connection
  const handleRefreshDatabase = async () => {
    setDbStatus({ ...dbStatus, checking: true });
    try {
      await axios.post(`${API}/database/reconnect`);
      await checkDatabaseStatus();
    } catch (error) {
      setDbStatus({ 
        connected: false, 
        checking: false,
        message: error.response?.data?.detail || 'Nie udało się połączyć' 
      });
    }
  };

  const locations = [
    { id: 'nowysacz', name: 'Nowy Sącz', icon: '🏔️' },
    { id: 'krakow', name: 'Kraków', icon: '🏰' },
    { id: 'grodek', name: 'Gródek nad Dunajcem', icon: '🌊' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!location) {
      setError('Wybierz lokal');
      return;
    }
    if (!username.trim() || !password.trim()) {
      setError('Wpisz login i hasło');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await api.login(username, password, location);
      setUserSession({ user: data.user, until: data.until }, rememberMe);
      
      showWelcomeToast(data.user.username);
      
      onLogin(data.user, location);
    } catch (e) {
      setError(e.response?.data?.detail || 'Błąd logowania');
    } finally {
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    // Sprawdź PIN w backendzie — dostajemy info o dopuszczonych lokalizacjach
    setDevPinError('');
    try {
      const r = await axios.get(`${API}/auth/dev-pin-info`, { params: { pin: devPin } });
      const locs = r.data?.locations || [];
      setDevAllowedLocations(locs);
      if (r.data?.kind === 'user' && locs.length === 1) {
        // User PIN → auto-wybór jedynej dostępnej lokalizacji
        setDevSelectedLocation(locs[0]);
        await handleDevLocationSelect(locs[0]);
      } else {
        setDevStep('location');
      }
    } catch (e) {
      setDevPinError(e.response?.data?.detail || 'Nieprawidłowy PIN');
      setTimeout(() => setDevPinError(''), 2000);
    }
  };

  const handleDevLocationSelect = async (locId) => {
    setDevSelectedLocation(locId);
    setDevUsersLoading(true);
    setDevUsers([]);
    try {
      const r = await axios.get(`${API}/auth/dev-users`, {
        params: { pin: devPin, location: locId },
      });
      setDevUsers(r.data || []);
      setDevStep('user');
      setDevUserSearch('');
    } catch (e) {
      setDevPinError(e.response?.data?.detail || 'Błąd pobierania listy użytkowników');
      setTimeout(() => setDevPinError(''), 2500);
    } finally {
      setDevUsersLoading(false);
    }
  };

  const handleDevImpersonate = async (devUsername) => {
    try {
      const r = await axios.post(`${API}/auth/dev-impersonate`, {
        pin: devPin,
        username: devUsername,
        location: devSelectedLocation,
      });
      setUserSession({ user: r.data.user, until: r.data.until }, false);
      showWelcomeToast(`${devUsername} (DEV)`);
      onLogin(r.data.user, devSelectedLocation);
      setShowDevPanel(false);
      setDevPin('');
      setDevStep('pin');
      setDevUsers([]);
      setDevSelectedLocation('');
    } catch (e) {
      setDevPinError(e.response?.data?.detail || 'Błąd impersonacji');
      setTimeout(() => setDevPinError(''), 2500);
    }
  };

  // Stary flow (Developer-superadmin) — zostawiamy jako fallback w UI dla kompatybilności
  const handleDevSuperLogin = (locId) => {
    const devUser = {
      id: 'dev_admin',
      username: 'Developer',
      role: 'manager',
      permissions: ['all']
    };
    setUserSession({ user: devUser, until: Date.now() + 86400000 }, false);
    showWelcomeToast('Developer');
    onLogin(devUser, locId);
    setShowDevPanel(false);
    setDevPin('');
    setDevStep('pin');
  };

  const selectedLocation = locations.find(l => l.id === location);

  return (
    <div 
      className="login-screen-fullbg" 
      data-testid="login-screen"
      style={{
        minHeight: '100vh',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${BG_LOGO_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        position: 'relative',
        padding: '20px'
      }}
    >
      {/* Elegant gradient overlay */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(135deg, rgba(13,27,42,0.95) 0%, rgba(27,38,59,0.92) 50%, rgba(13,27,42,0.95) 100%)',
        backdropFilter: 'blur(3px)',
        zIndex: 1,
        pointerEvents: 'none'
      }} />
      
      {/* Animated circles */}
      <div style={{
        position: 'fixed',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, transparent 70%)',
        top: '-250px',
        left: '-250px',
        zIndex: 1,
        animation: 'pulse 8s ease-in-out infinite',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 70%)',
        bottom: '-200px',
        right: '-200px',
        zIndex: 1,
        animation: 'pulse 6s ease-in-out infinite',
        pointerEvents: 'none'
      }} />
      
      {/* Main login card */}
      <div style={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        maxWidth: location ? '480px' : '900px',
        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {!location ? (
          // Location Selection Screen
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '48px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 100px rgba(212, 175, 55, 0.1)',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            {/* Logo and Header */}
            <div style={{
              textAlign: 'center', 
              marginBottom: '40px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={LOGO_URL} 
                alt="MR JERRY" 
                style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  border: '3px solid rgba(212,175,55,0.5)',
                  boxShadow: '0 8px 32px rgba(212,175,55,0.3)',
                  marginBottom: '20px',
                  animation: 'float 3s ease-in-out infinite',
                  display: 'block'
                }}
              />
              <div style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.7)',
                letterSpacing: '3px',
                textTransform: 'uppercase',
                marginBottom: '12px',
                fontWeight: '600'
              }}>
                System Zarządzania
              </div>
              <h1 style={{
                fontSize: '48px',
                fontWeight: '800',
                color: '#fff',
                margin: '0 0 8px 0',
                textShadow: '0 4px 20px rgba(0,0,0,0.5)',
                letterSpacing: '2px'
              }}>
                MR JERRY
              </h1>
              <div style={{
                fontSize: '16px',
                color: 'rgba(212,175,55,0.9)',
                fontWeight: '500'
              }}>
                Nowy Sącz • Kraków • Gródek n/D
              </div>
            </div>

            {/* Location Cards */}
            <div>
              <h2 style={{
                fontSize: '20px',
                color: '#fff',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: '600'
              }}>
                Wybierz lokal
              </h2>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '16px',
                justifyContent: 'center',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                {locations.map((loc) => (
                  <button
                    key={loc.id}
                    onClick={() => setLocation(loc.id)}
                    data-testid={`location-card-${loc.id}`}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid rgba(212,175,55,0.3)',
                      borderRadius: '16px',
                      padding: '24px 16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      color: '#fff',
                      fontSize: '15px',
                      fontWeight: '600',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '10px',
                      minWidth: '180px',
                      flex: '0 0 180px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(212,175,55,0.15)';
                      e.currentTarget.style.borderColor = 'rgba(212,175,55,0.8)';
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(212,175,55,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(212,175,55,0.3)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{fontSize: '40px'}}>{loc.icon}</div>
                    <div>{loc.name}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Version badge */}
            <div style={{
              marginTop: '24px',
              padding: '12px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.6)'
            }}>
              <span>{APP_VERSION}</span>
              <span>•</span>
              <span>© 2026 MR JERRY</span>
            </div>
          </div>
        ) : (
          // Login Form
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            padding: '48px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 100px rgba(212, 175, 55, 0.1)',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            {/* Logo */}
            <div style={{
              textAlign: 'center', 
              marginBottom: '32px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={LOGO_URL} 
                alt="MR JERRY" 
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: '3px solid rgba(212,175,55,0.5)',
                  boxShadow: '0 8px 32px rgba(212,175,55,0.3)',
                  marginBottom: '16px',
                  display: 'block'
                }}
              />
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#fff',
                margin: 0
              }}>
                Zaloguj się
              </h2>
              <div style={{
                fontSize: '14px',
                color: 'rgba(255,255,255,0.6)',
                marginTop: '8px'
              }}>
                {selectedLocation?.icon} {selectedLocation?.name}
              </div>
            </div>

            {/* Login Form */}
            <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
              {/* Back button */}
              <button
                type="button"
                onClick={() => {
                  setLocation('');
                  setError('');
                }}
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '12px',
                  color: 'rgba(212,175,55,0.9)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(212,175,55,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                }}
              >
                ← Zmień lokal
              </button>

              {/* Username */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Login
                </label>
                <input
                  type="text"
                  placeholder="Wpisz login"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  data-testid="login-username"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '15px',
                    outline: 'none',
                    transition: 'all 0.3s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  }}
                />
              </div>

              {/* Password */}
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Hasło
                </label>
                <div style={{position: 'relative'}}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Wpisz hasło"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password"
                    style={{
                      width: '100%',
                      padding: '14px 48px 14px 16px',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '15px',
                      outline: 'none',
                      transition: 'all 0.3s',
                      boxSizing: 'border-box'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(212,175,55,0.6)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(212,175,55,0.7)',
                      cursor: 'pointer',
                      fontSize: '20px',
                      padding: '4px 8px'
                    }}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div style={{
                  padding: '12px 16px',
                  background: 'rgba(244,67,54,0.15)',
                  border: '1px solid rgba(244,67,54,0.3)',
                  borderRadius: '12px',
                  color: '#ff6b6b',
                  fontSize: '14px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              {/* Remember Me Checkbox */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '4px 0'
              }}>
                <input
                  type="checkbox"
                  id="rememberMe"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#d4af37'
                  }}
                />
                <label
                  htmlFor="rememberMe"
                  style={{
                    color: 'rgba(255,255,255,0.85)',
                    fontSize: '14px',
                    cursor: 'pointer',
                    userSelect: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>🔒 Zapamiętaj mnie</span>
                  <span style={{
                    fontSize: '11px',
                    color: 'rgba(255,255,255,0.5)',
                    fontStyle: 'italic'
                  }}>
                    (pozostaniesz zalogowany)
                  </span>
                </label>
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: loading ? 'rgba(212,175,55,0.5)' : 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#1a1a1a',
                  fontSize: '16px',
                  fontWeight: '700',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: loading ? 'none' : '0 8px 24px rgba(212,175,55,0.4)',
                  marginTop: '8px'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(212,175,55,0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(212,175,55,0.4)';
                  }
                }}
              >
                {loading ? '⏳ Logowanie...' : '🔓 Zaloguj się'}
              </button>

              {/* Register Button */}
              {/* Register + Form buttons */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginTop: '12px'
              }}>
                <button
                  type="button"
                  onClick={() => setShowRegistration(true)}
                  disabled={loading}
                  style={{
                    padding: '16px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '14px',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                    }
                  }}
                >
                  👤 Załóż konto
                </button>

                <button
                  type="button"
                  onClick={() => setShowEmployeeForm(true)}
                  disabled={loading}
                  style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    borderRadius: '14px',
                    color: 'var(--gold)',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(212, 175, 55, 0.3) 0%, rgba(212, 175, 55, 0.15) 100%)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)';
                    }
                  }}
                >
                  📋 Formularz
                </button>
              </div>

              {/* Szybkie akcje — okrągłe ikony (Strata kuchenna, Strata szkła, Jerry Drive) */}
              <div style={{
                marginTop: 18,
                display: 'flex',
                gap: 18,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}>
                {/* Szybkie dodanie straty kuchennej */}
                <CircleAction
                  testId="login-quick-loss-btn"
                  label="Strata kuchenna"
                  emoji="🔥"
                  color="#ef4444"
                  onClick={() => setShowQuickLoss(true)}
                  disabled={loading}
                />

                {/* Szybkie dodanie ruchu szkła */}
                <CircleAction
                  testId="login-quick-glass-btn"
                  label="Strata szkła"
                  emoji="🥃"
                  color="#f97316"
                  onClick={() => setShowQuickGlassLoss(true)}
                  disabled={loading}
                />

                {/* Jerry Drive — TYLKO Nowy Sącz */}
                {location === 'nowysacz' && (
                  <CircleAction
                    testId="login-jerry-drive-btn"
                    label="Jerry Drive"
                    emoji="🚗"
                    color="#3b82f6"
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/jerry-drive/access?location=nowysacz`);
                        if (r.data?.isSet) {
                          setJdLock({ open: true, type: r.data.type || 'pin', error: '' });
                        } else {
                          setShowJerryDrive(true);
                        }
                      } catch (e) {
                        setShowJerryDrive(true);
                      }
                    }}
                    disabled={loading}
                  />
                )}
              </div>
            </form>

            {/* Footer */}
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '12px',
              textAlign: 'center',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.5)',
              borderTop: '1px solid rgba(255,255,255,0.05)'
            }}>
              <div>{APP_VERSION} • © 2026 MR JERRY</div>
              <div style={{marginTop: '4px', fontSize: '11px'}}>
                Napiwki pod kontrolą
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.1);
          }
        }
      `}</style>

      {/* Registration Modal */}
      {showRegistration && location && (
        <div className="registration-form-overlay" onClick={() => setShowRegistration(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <RegistrationForm
              location={location}
              onClose={() => setShowRegistration(false)}
              onSuccess={() => setShowRegistration(false)}
            />
          </div>
        </div>
      )}

      {/* Employee Form Modal */}
      {showEmployeeForm && location && (
        <div className="registration-form-overlay" onClick={() => setShowEmployeeForm(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <EmployeeFormModal
              location={location}
              onClose={() => setShowEmployeeForm(false)}
            />
          </div>
        </div>
      )}

      {/* Quick Loss Modal */}
      <QuickLossModal 
        isOpen={showQuickLoss} 
        onClose={() => setShowQuickLoss(false)}
        location={location}
      />

      {/* Quick Glass Loss Modal */}
      <QuickGlassLossModal
        isOpen={showQuickGlassLoss}
        onClose={() => setShowQuickGlassLoss(false)}
        location={location}
      />

      {/* Problem Report Modal */}
      <ProblemReportModal 
        isOpen={showProblemReport} 
        onClose={() => setShowProblemReport(false)}
      />

      {/* Jerry Drive Modal (publiczny widok kierowcy) */}
      {showJerryDrive && (
        <div
          data-testid="jerry-drive-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10000,
            overflowY: 'auto',
            backdropFilter: 'blur(6px)',
          }}
        >
          <JerryDrivePublic onClose={() => setShowJerryDrive(false)} />
        </div>
      )}

      {/* Jerry Drive Access Lock (PIN / Gest) */}
      {jdLock.open && (
        <div
          data-testid="jerry-drive-lock-modal"
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: 'rgba(2,6,23,0.92)',
            backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setJdLock({ open: false, type: null, error: '' }); }}
        >
          <div style={{
            background: 'linear-gradient(180deg, rgba(17,24,39,0.95), rgba(2,6,23,0.95))',
            borderRadius: 20, padding: 24, maxWidth: 420, width: '100%',
            border: '1px solid rgba(6,182,212,0.25)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
          }}>
            <JerryDriveAccessLock
              mode="enter"
              allowedType={jdLock.type}
              title="🚗 Jerry Drive — kod dostępu"
              errorMsg={jdLock.error}
              onCancel={() => setJdLock({ open: false, type: null, error: '' })}
              onSuccess={async (value /* , type */) => {
                setJdLock((s) => ({ ...s, error: '' }));
                try {
                  const r = await axios.post(`${API}/jerry-drive/access/verify?location=nowysacz`, { value });
                  if (r.data?.valid) {
                    setJdLock({ open: false, type: null, error: '' });
                    setShowJerryDrive(true);
                  } else {
                    setJdLock((s) => ({ ...s, error: 'Niepoprawny kod / gest' }));
                  }
                } catch (e) {
                  setJdLock((s) => ({ ...s, error: 'Błąd serwera' }));
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Floating Problem Report Button - Bottom Right Corner */}
      <button
        onClick={() => setShowProblemReport(true)}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
          border: '3px solid rgba(255, 255, 255, 0.2)',
          color: '#fff',
          fontSize: '28px',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.3)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          animation: 'pulse 2s infinite'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.15) rotate(5deg)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(239, 68, 68, 0.7), 0 0 60px rgba(239, 68, 68, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(239, 68, 68, 0.5), 0 0 40px rgba(239, 68, 68, 0.3)';
        }}
        title="Zgłoś sprawę do managera"
      >
        🐛
      </button>

      {/* Floating Matters FAB (publiczne zgłoszenie sprawy do managera) */}
      <MattersFab anonymous fixedRight={100} location={location} />

      {/* Tooltip for Problem Button */}
      <div style={{
        position: 'fixed',
        bottom: '100px',
        right: '24px',
        background: 'rgba(0, 0, 0, 0.9)',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '600',
        zIndex: 999,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        pointerEvents: 'none',
        opacity: 0.9,
        whiteSpace: 'nowrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🐛</span>
          <span>Zgłoś sprawę</span>
        </div>
        <div style={{
          position: 'absolute',
          bottom: '-8px',
          right: '24px',
          width: '0',
          height: '0',
          borderLeft: '8px solid transparent',
          borderRight: '8px solid transparent',
          borderTop: '8px solid rgba(0, 0, 0, 0.9)'
        }} />
      </div>

      {/* Developer Panel Button */}
      <button
        onClick={() => setShowDevPanel(true)}
        style={{
          position: 'fixed',
          bottom: '24px',
          left: '24px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          border: 'none',
          color: '#fff',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 8px 24px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.3)',
          zIndex: 1000,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.15) rotate(-5deg)';
          e.currentTarget.style.boxShadow = '0 12px 32px rgba(139, 92, 246, 0.7), 0 0 60px rgba(139, 92, 246, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
          e.currentTarget.style.boxShadow = '0 8px 24px rgba(139, 92, 246, 0.5), 0 0 40px rgba(139, 92, 246, 0.3)';
        }}
        title="Panel Programisty"
        data-testid="dev-panel-trigger"
      >
        👨‍💻
      </button>

      {/* Database status — mała kropka obok ikony programisty */}
      <div
        title={dbStatus.checking ? 'Sprawdzanie bazy danych...' : dbStatus.connected ? 'Baza danych: OK' : (dbStatus.message || 'Brak połączenia z bazą')}
        data-testid="db-status-dot"
        style={{
          position: 'fixed',
          bottom: '40px',
          left: '94px',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          background: dbStatus.checking ? '#9ca3af' : dbStatus.connected ? '#22c55e' : '#ef4444',
          boxShadow: dbStatus.checking
            ? '0 0 6px rgba(156, 163, 175, 0.6)'
            : dbStatus.connected
              ? '0 0 10px rgba(34, 197, 94, 0.7)'
              : '0 0 10px rgba(239, 68, 68, 0.7)',
          border: '2px solid rgba(13, 27, 42, 0.85)',
          zIndex: 1001,
          animation: dbStatus.checking ? 'pulse 1.5s infinite' : 'none',
          pointerEvents: 'auto',
        }}
      />

      {/* Developer Panel Modal */}
      {showDevPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(10px)'
        }}
        onClick={() => { setShowDevPanel(false); setDevStep('pin'); setDevPin(''); setDevPinError(''); setDevAllowedLocations(null); if (onDevPanelClose) onDevPanelClose(); }}
        >
          <div style={{
            background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            border: '2px solid rgba(139, 92, 246, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{
              margin: '0 0 24px',
              fontSize: '28px',
              fontWeight: '700',
              color: '#fff',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <span>👨‍💻</span>
              <span>Panel Programisty</span>
            </h2>

            {devStep === 'pin' && (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.7)'
                  }}>
                    Wpisz PIN:
                  </label>
                  <input
                    type="password"
                    value={devPin}
                    onChange={(e) => setDevPin(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDevLogin()}
                    placeholder="••••••••"
                    autoFocus
                    data-testid="dev-pin-input"
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `2px solid ${devPinError ? '#ef4444' : 'rgba(139, 92, 246, 0.3)'}`,
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.3s'
                    }}
                  />
                  {devPinError && (
                    <div style={{
                      marginTop: '8px',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      ❌ {devPinError}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleDevLogin}
                    data-testid="dev-pin-submit"
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'transform 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    🔓 Odblokuj
                  </button>
                  <button
                    onClick={() => {
                      setShowDevPanel(false);
                      setDevPin('');
                      setDevPinError('');
                      setDevStep('pin');
                      if (onDevPanelClose) onDevPanelClose();
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: '#fff',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
                  >
                    Anuluj
                  </button>
                </div>

                <div style={{
                  marginTop: '20px',
                  padding: '12px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  textAlign: 'center'
                }}>
                  💡 Backdoor dla developera - wejście bez logowania
                </div>
              </>
            )}

            {devStep === 'location' && (
              <>
                <div style={{
                  marginBottom: '20px',
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'center'
                }}>
                  ✅ PIN poprawny. Wybierz lokal, w którym chcesz pracować:
                </div>

                <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
                  {locations
                    .filter((loc) => !devAllowedLocations || devAllowedLocations.includes(loc.id))
                    .map((loc) => (
                    <button
                      key={loc.id}
                      onClick={() => handleDevLocationSelect(loc.id)}
                      data-testid={`dev-location-${loc.id}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        padding: '14px 18px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        color: '#fff',
                        border: '2px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '12px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.25)';
                        e.currentTarget.style.borderColor = '#8b5cf6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
                      }}
                    >
                      <span style={{ fontSize: '28px' }}>{loc.icon}</span>
                      <span>{loc.name}</span>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setDevStep('pin');
                    setDevPin('');
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'rgba(255, 255, 255, 0.7)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '10px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  ← Wstecz
                </button>
              </>
            )}

            {devStep === 'user' && (
              <>
                <div style={{
                  marginBottom: '14px',
                  fontSize: '13px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  textAlign: 'center',
                }}>
                  ✅ Lokal: <strong style={{ color: '#a78bfa' }}>{locations.find((l) => l.id === devSelectedLocation)?.name}</strong>
                  <div style={{ marginTop: 4, fontSize: 11 }}>
                    Wybierz użytkownika, jako który chcesz się zalogować:
                  </div>
                </div>

                <input
                  type="text"
                  value={devUserSearch}
                  onChange={(e) => setDevUserSearch(e.target.value)}
                  placeholder="🔍 Szukaj użytkownika..."
                  data-testid="dev-user-search"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    marginBottom: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />

                {devUsersLoading ? (
                  <div style={{ textAlign: 'center', color: '#a78bfa', padding: '20px' }}>
                    Ładuję listę użytkowników...
                  </div>
                ) : devUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', padding: '20px' }}>
                    Brak aktywnych użytkowników w tej lokalizacji.
                  </div>
                ) : (
                  <div
                    data-testid="dev-users-list"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      marginBottom: '14px',
                      maxHeight: '320px',
                      overflowY: 'auto',
                      paddingRight: '4px',
                    }}
                  >
                    {devUsers
                      .filter((u) => {
                        const q = devUserSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          (u.username || '').toLowerCase().includes(q) ||
                          (u.firstName || '').toLowerCase().includes(q) ||
                          (u.lastName || '').toLowerCase().includes(q) ||
                          (u.role || '').toLowerCase().includes(q)
                        );
                      })
                      .map((u) => {
                        const roleColor =
                          u.role === 'manager' ? '#10b981' :
                          u.role === 'admin' ? '#f59e0b' :
                          u.role === 'developer' ? '#a78bfa' : '#64748b';
                        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
                        return (
                          <button
                            key={u.id}
                            onClick={() => handleDevImpersonate(u.username)}
                            data-testid={`dev-user-${u.username}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '10px',
                              padding: '10px 14px',
                              background: 'rgba(139, 92, 246, 0.08)',
                              color: '#fff',
                              border: '1px solid rgba(139, 92, 246, 0.25)',
                              borderRadius: '10px',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.22)';
                              e.currentTarget.style.borderColor = '#8b5cf6';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'rgba(139, 92, 246, 0.08)';
                              e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.25)';
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <span style={{ fontWeight: 700 }}>{u.username}</span>
                                {fullName && (
                                  <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.55)' }}>
                                    ({fullName})
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                {u.permissionsCount} uprawnień
                              </div>
                            </div>
                            <span style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '3px 8px',
                              borderRadius: 999,
                              background: roleColor + '22',
                              color: roleColor,
                              border: `1px solid ${roleColor}66`,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              whiteSpace: 'nowrap',
                            }}>
                              {u.role}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}

                {devPinError && (
                  <div style={{ marginBottom: 10, color: '#ef4444', fontSize: 12, textAlign: 'center' }}>
                    ❌ {devPinError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => handleDevSuperLogin(devSelectedLocation)}
                    data-testid="dev-superadmin-login"
                    title="Zaloguj jako wirtualny Developer (wszystkie uprawnienia)"
                    style={{
                      flex: 1,
                      padding: '11px',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '700',
                      cursor: 'pointer',
                    }}
                  >
                    👑 Developer (all)
                  </button>
                  <button
                    onClick={() => {
                      setDevStep('location');
                      setDevUsers([]);
                      setDevUserSearch('');
                    }}
                    style={{
                      flex: 1,
                      padding: '11px',
                      background: 'rgba(255, 255, 255, 0.08)',
                      color: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    ← Wstecz
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// CircleAction — okrągły przycisk szybkiej akcji na ekranie logowania
// ============================================================================
const CircleAction = ({ testId, label, emoji, color, onClick, disabled }) => {
  const [hover, setHover] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        aria-label={label}
        style={{
          width: 72, height: 72, borderRadius: '50%',
          background: `radial-gradient(circle at 30% 30%, ${color}33, ${color}14)`,
          border: `2px solid ${hover && !disabled ? color : `${color}88`}`,
          color: '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 30,
          transition: 'transform .18s ease, box-shadow .18s ease, border-color .18s ease',
          transform: hover && !disabled ? 'translateY(-3px) scale(1.04)' : 'translateY(0) scale(1)',
          boxShadow: hover && !disabled
            ? `0 12px 28px ${color}55, 0 0 0 4px ${color}22`
            : `0 4px 14px ${color}33`,
          opacity: disabled ? 0.45 : 1,
        }}
      >
        <span style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }}>{emoji}</span>
      </button>
      <span style={{
        fontSize: 11, fontWeight: 700, color: '#cbd5e1',
        textAlign: 'center', maxWidth: 88, lineHeight: 1.2,
      }}>
        {label}
      </span>
    </div>
  );
};

export default LoginScreen;
